import { MutationType, STPageType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { BaseMutation } from '../base/base-mutation';
import { Rule } from '../base/mutation-rules';
import type { SlideDataModel } from '../../model/slide-data-model';
import type { MaybeError } from '../base/base-mutation';
import type { IValidAddPageMutationData, IValidRemovePageMutationData } from '../base/valid-mutation-data';
import type { PresentationDataModel } from '../../model/presentation-data-model';

export class RemovePageMutation extends BaseMutation<PresentationDataModel, IValidRemovePageMutationData> {
    public readonly type: MutationType = MutationType.RemovePage;

    public apply(presentation: PresentationDataModel) {
        const { pageType, pageId, index } = this.data;
        if (pageType === STPageType.slide) {
            presentation.store.removeSlideByIndex(index);
            return;
        }

        if (pageType === STPageType.slideLayout) {
            presentation.store.removeLayoutByIndex(pageId, index);
            return;
        }

        if (pageType === STPageType.slideMaster) {
            presentation.store.removeMasterByIndex(index);
            return;
        }

        if (pageType === STPageType.notes) {
            const slide = presentation.store.getNoteSlideById(pageId)?.getSlide() as SlideDataModel | undefined;
            slide?.store.updateNoteSlideId(null);
            presentation.store.removeNoteSlide(pageId);
            return;
        }

        if (pageType === STPageType.notesMaster) {
            presentation.store.removeNotesMaster(pageId);
            return;
        }
    }

    public invert(presentation: PresentationDataModel): [IValidAddPageMutationData] {
        const { pageType, pageId, index, parentId } = this.data;
        const page = presentation.store.getPageByIdAndType(pageId, pageType);
        assert(page, 'page should exist');
        const mutData: IValidAddPageMutationData = {
            type: MutationType.AddPage,
            pageType,
            pageId,
            shapeTreeId: page.store.getShapeTree().id,
            index,
        };
        if (pageType === STPageType.slide) {
            mutData.slideOptions = { layoutId: parentId };
        }
        if (pageType === STPageType.slideLayout) {
            mutData.slideLayoutOptions = { masterId: parentId };
        }
        if (pageType === STPageType.slideMaster) {
            mutData.slideMasterOptions = { themeId: parentId };
        }
        if (pageType === STPageType.notes) {
            mutData.notesOptions = { slideId: parentId };
        }
        if (pageType === STPageType.notesMaster) {
            mutData.notesMasterOptions = { themeId: parentId };
        }
        return [mutData];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageType, pageId, parentId, index } = this.data;
        if (pageType === STPageType.slide) {
            return Rule.every(
                () => Rule.pageList.should.containSlideInPosition(presentation, index, pageId),
                () => Rule.page.should.haveNoStaleReference(presentation, pageId),
            );
        }

        if (pageType === STPageType.slideLayout) {
            return Rule.every(
                () => Rule.pageList.should.containLayoutInPosition(presentation, parentId, index, pageId),
                () => Rule.page.should.haveNoStaleReference(presentation, pageId),
            );
        }

        if (pageType === STPageType.slideMaster) {
            return Rule.every(
                () => Rule.pageList.should.containMasterInPosition(presentation, index, pageId),
                () => Rule.page.should.haveNoStaleReference(presentation, pageId),
            );
        }

        if (pageType === STPageType.notes) {
            return Rule.every(() => Rule.page.should.exist(presentation, parentId, STPageType.slide));
        }
    }
}
